// In src/ because CRA blocks runtime imports from outside it; api/ imports it from here.

export const LAYER_ENVIRONMENTS = [
  'production',
  'production-ca',
  'staging',
  'sandbox',
] as const

export type LayerEnvironment = (typeof LAYER_ENVIRONMENTS)[number]

export const isLayerEnvironment = (value: unknown): value is LayerEnvironment =>
  typeof value === 'string'
  && (LAYER_ENVIRONMENTS as readonly string[]).includes(value)

type LayerReactEnvironment = 'production' | 'production-ca' | 'staging' | 'sandbox'

type LayerEnvironmentConfig = {
  // LayerProvider requires one of its own environment names even with an override.
  layerReactEnvironment: LayerReactEnvironment
  apiUrl: string
  authUrl: string
  scope: string
  usePlaidSandbox: boolean
}

export const LAYER_ENVIRONMENT_CONFIGS: Record<LayerEnvironment, LayerEnvironmentConfig> = {
  production: {
    layerReactEnvironment: 'production',
    apiUrl: 'https://api.layerfi.com',
    authUrl: 'https://auth.layerfi.com/oauth2/token',
    scope: 'https://api.layerfi.com/production',
    usePlaidSandbox: false,
  },
  'production-ca': {
    layerReactEnvironment: 'production-ca',
    apiUrl: 'https://api-ca.layerfi.com',
    authUrl: 'https://auth.layerfi.com/oauth2/token',
    scope: 'https://api-ca.layerfi.com/production',
    usePlaidSandbox: false,
  },
  staging: {
    layerReactEnvironment: 'staging',
    apiUrl: 'https://staging.layerfi.com',
    authUrl: 'https://auth.layerfi.com/oauth2/token',
    scope: 'https://sandbox.layerfi.com/sandbox',
    usePlaidSandbox: true,
  },
  sandbox: {
    layerReactEnvironment: 'sandbox',
    apiUrl: 'https://sandbox.layerfi.com',
    authUrl: 'https://auth.layerfi.com/oauth2/token',
    scope: 'https://sandbox.layerfi.com/sandbox',
    usePlaidSandbox: true,
  },
}

export type ResolvedLayerEnvironmentConfig = LayerEnvironmentConfig & {
  environment: LayerEnvironment
}

export const getDeploymentLayerEnvironment = (): LayerEnvironment => {
  const environment = process.env.REACT_APP_ENVIRONMENT
  if (environment === 'internalStaging') return 'staging'
  return isLayerEnvironment(environment) ? environment : 'sandbox'
}

export const getLayerEnvironmentConfig = (
  override?: LayerEnvironment | null,
): ResolvedLayerEnvironmentConfig => {
  if (override) {
    return { environment: override, ...LAYER_ENVIRONMENT_CONFIGS[override] }
  }

  const environment = getDeploymentLayerEnvironment()
  const config = LAYER_ENVIRONMENT_CONFIGS[environment]
  const usePlaidSandbox = process.env.REACT_APP_USE_PLAID_SANDBOX

  return {
    environment,
    ...config,
    apiUrl: process.env.REACT_APP_LAYER_API_URL_OVERRIDE ?? config.apiUrl,
    scope: process.env.REACT_APP_LAYER_SCOPE_OVERRIDE ?? config.scope,
    usePlaidSandbox: usePlaidSandbox
      ? usePlaidSandbox !== 'false'
      : config.usePlaidSandbox,
  }
}

export const toEnvironmentConfigOverride = (
  config: ResolvedLayerEnvironmentConfig,
) => ({
  environment: config.layerReactEnvironment,
  apiUrl: config.apiUrl,
  authUrl: config.authUrl,
  scope: config.scope,
})
